import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { es } from './es';
import { ca } from './ca';

const dictionaries = { es, ca };

// Deep merge: base (es) overridden by override (ca/en) where keys exist
const deepMerge = (base, override) => {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) || Array.isArray(override)) return override ?? base;
  if (typeof base !== 'object' || typeof override !== 'object') return override;
  const out = { ...base };
  for (const k of Object.keys(override)) {
    if (k in base) {
      out[k] = deepMerge(base[k], override[k]);
    } else {
      out[k] = override[k];
    }
  }
  return out;
};

const I18nContext = createContext({
  lang: 'es',
  t: (k) => k,
  setLang: () => {},
  content: es,
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'es';
    return localStorage.getItem('vidat_lang') || 'es';
  });

  const setLang = (l) => {
    setLangState(l);
    if (typeof window !== 'undefined') {
      localStorage.setItem('vidat_lang', l);
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const content = useMemo(() => {
    const dict = dictionaries[lang] || es;
    if (lang === 'es') return es;
    return deepMerge(es, dict);
  }, [lang]);

  const t = (path) => {
    if (!path) return '';
    const parts = path.split('.');
    let cur = content;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
      else return path;
    }
    return cur;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t, content }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
